import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { X, Mail, Lock, User, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

export const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(formData.email, formData.password);
        toast.success(t('auth.loginSuccess'));
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success(t('auth.registerSuccess'));
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center text-[#2D2D2D]">
            {tab === 'login' ? t('auth.login') : t('auth.register')}
          </DialogTitle>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setTab('login')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'login' ? 'border-[#2F4F4F] text-[#2F4F4F]' : 'border-transparent text-[#8A8A8A]'
            }`}
            data-testid="tab-login"
          >
            {t('auth.login')}
          </button>
          <button
            onClick={() => setTab('register')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'register' ? 'border-[#2F4F4F] text-[#2F4F4F]' : 'border-transparent text-[#8A8A8A]'
            }`}
            data-testid="tab-register"
          >
            {t('auth.register')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === 'register' && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
              <Input
                type="text"
                placeholder={t('auth.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="pl-10 h-12 rounded-xl"
                required
                data-testid="input-name"
              />
            </div>
          )}
          
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
            <Input
              type="email"
              placeholder={t('auth.email')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="pl-10 h-12 rounded-xl"
              required
              data-testid="input-email"
            />
          </div>
          
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
            <Input
              type="password"
              placeholder={t('auth.password')}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pl-10 h-12 rounded-xl"
              required
              minLength={6}
              data-testid="input-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white"
            data-testid="submit-auth"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              tab === 'login' ? t('auth.loginBtn') : t('auth.registerBtn')
            )}
          </Button>
        </form>

        <p className="text-center text-sm text-[#8A8A8A] mt-4">
          {tab === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
          <button
            onClick={() => setTab(tab === 'login' ? 'register' : 'login')}
            className="text-[#2F4F4F] font-medium hover:underline"
          >
            {tab === 'login' ? t('auth.register') : t('auth.login')}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
